import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Estoque.module.css';
import { listar, COLECOES } from '../../services/firestore';
import {
  calcularStatusEstoque,
  posicaoDeveAparecer,
  labelStatus,
  STATUS,
} from '../../utils/estoque';

// ====================================================================
// ESTOQUE — versão final (Bloco 6 fechado)
// Le produtos + estoque_posicao + estoque_limites + movimentacoes.
// Mostra linhas "Em transferencia" lendo movimentacoes EM_TRANSITO.
// Drawer: Movimentacoes do produto, QR (placeholder), Documentos com
// botoes reais visualizar/baixar (Storage URLs).
// ====================================================================

function formatarNumero(num) {
  return (Number(num) || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function horasDecorridas(ts) {
  if (!ts) return 0;
  try {
    const t = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return (Date.now() - t.getTime()) / (1000 * 60 * 60);
  } catch {
    return 0;
  }
}

function formatarPrazo(h) {
  if (h < 0) return 'vencido';
  if (h < 1) return Math.round(h * 60) + ' min';
  if (h < 24) return Math.round(h) + 'h';
  return Math.round(h / 24) + ' dias';
}

function formatarDecorrido(ts) {
  if (!ts) return '';
  const h = horasDecorridas(ts);
  if (h < 1) return 'há ' + Math.round(h * 60) + ' min';
  if (h < 24) return 'há ' + Math.round(h) + 'h';
  return 'há ' + Math.round(h / 24) + ' dias';
}

function formatarDataMov(ts) {
  if (!ts) return '';
  try {
    const t = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return t.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return '';
  }
}

const STATUS_CLASS = {
  OK: 'stOk',
  'Abaixo mín.': 'stBaixo',
  Vencido: 'stVencido',
  Trânsito: 'stTransito',
  // Novos estados (util estoque - 5 estados)
  ZERADO: 'stZerado',
  CRITICO: 'stCritico',
  ATENCAO: 'stAtencao',
  ACIMA_MAX: 'stAcima',
};

const MOV_TAG_CLASS = {
  SAIDA: 'mtSaida',
  ENTRADA: 'mtEntrada',
  TRANSFERENCIA: 'mtTransf',
  DEVOLUCAO: 'mtDevolucao',
  ESTORNO: 'mtTransf',
};

const MOV_LABEL = {
  SAIDA: 'SAÍDA',
  ENTRADA: 'ENTRADA',
  TRANSFERENCIA: 'TRANSF.',
  DEVOLUCAO: 'DEVOLUÇÃO',
  ESTORNO: 'ESTORNO',
};

export default function Estoque() {
  const navigate = useNavigate();

  const [produtos, setProdutos] = useState([]);
  const [posicoes, setPosicoes] = useState([]);
  const [limites, setLimites] = useState([]);
  const [estoquesData, setEstoquesData] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [parametros, setParametros] = useState({ prazoAceiteHoras: 48 });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [expandidos, setExpandidos] = useState(new Set());
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('mov');

  const [busca, setBusca] = useState('');
  const [filtroResina, setFiltroResina] = useState('');
  const [filtroEstoque, setFiltroEstoque] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCor, setFiltroCor] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [prods, pos, lim, movs, params, ests] = await Promise.all([
        listar(COLECOES.PRODUTOS, { ordenarPor: 'cor' }),
        listar(COLECOES.ESTOQUE_POSICAO),
        listar(COLECOES.ESTOQUE_LIMITES),
        listar(COLECOES.MOVIMENTACOES),
        listar(COLECOES.PARAMETROS),
        listar(COLECOES.ESTOQUES),
      ]);
      setProdutos(prods.filter((p) => p.ativo !== false));
      setPosicoes(pos);
      setLimites(lim);
      setEstoquesData(ests);
      setMovimentacoes(movs);
      if (params.length > 0) {
        setParametros({
          prazoAceiteHoras: Number(params[0].prazoAceiteHoras) || 48,
        });
      }
    } catch (e) {
      setErro(e.message || 'Erro ao carregar estoque.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const emTransito = useMemo(
    () => movimentacoes.filter((m) => m.status === 'EM_TRANSITO'),
    [movimentacoes]
  );

  const produtosComPosicoes = useMemo(() => {
    return produtos.map((p) => {
      const posDoProd = posicoes.filter((pos) => pos.produtoId === p.id);
      const limDoProd = limites.filter((l) => l.produtoId === p.id);

      const posicoesUI = posDoProd
        .map((pos) => {
          // 1) Fonte primária: niveis[] dentro do documento do estoque (cadastrado em
          //    Cadastros > Estoques). É onde o usuário realmente cadastra mín/máx.
          const estoqueDoc = estoquesData.find((e) => e.id === pos.estoqueId);
          const nivelDoProduto = (estoqueDoc?.niveis || []).find(
            (n) => n.produtoId === p.id
          );
          // 2) Fallback: coleção legada estoque_limites (caso ainda existam dados antigos)
          const limLegado = limDoProd.find((l) => l.estoqueId === pos.estoqueId);
          // Prioriza o nível cadastrado; se não houver, usa o legado; senão, 0
          const min = Number(nivelDoProduto?.minimo ?? limLegado?.minimo) || 0;
          const max = Number(nivelDoProduto?.maximo ?? limLegado?.maximo) || 0;
          const qtd = Number(pos.qtdReal) || 0;
          // Calcula status detalhado via util (5 estados)
          const statusKey = calcularStatusEstoque(qtd, min, max);
          // Mantém o label legado em "status" para compatibilidade com o código
          // que ainda compara strings ("Abaixo mín.", "OK"). Filtros e contadores
          // novos usam o campo statusKey.
          let statusLegado = 'OK';
          if (statusKey === STATUS.CRITICO || statusKey === STATUS.ZERADO) {
            statusLegado = 'Abaixo mín.';
          }
          return {
            tipo: 'real',
            estoqueId: pos.estoqueId,
            estoque: pos.estoqueNome || '(sem nome)',
            qtd,
            volume: qtd,
            status: statusLegado,
            statusKey,
            statusLabel: labelStatus(statusKey),
            minimo: min,
            maximo: max,
          };
        })
        // Regra do util: esconde posições zeradas SEM mínimo (não polui a tela);
        // posições zeradas COM mínimo definido aparecem como alerta.
        .filter((pos) => posicaoDeveAparecer(pos.qtd, pos.minimo));

      const transitoUI = [];
      emTransito.forEach((mov) => {
        const itensDoProd = (mov.itens || []).filter(
          (it) => it.produtoId === p.id
        );
        itensDoProd.forEach((it) => {
          const horasDec = horasDecorridas(mov.criadoEm);
          const prazo =
            Number(mov.prazoHoras) || parametros.prazoAceiteHoras;
          const horasRest = prazo - horasDec;
          transitoUI.push({
            tipo: 'transito',
            qtd: it.qtd || 0,
            volume: it.qtd || 0,
            status: 'Trânsito',
            estoque: 'Em transferência',
            transito: {
              movimentacaoId: mov.id,
              tipoMov: mov.tipo,
              origem: mov.estoqueOrigemNome || '',
              destino: mov.estoqueDestinoNome || '',
              iniciador: mov.responsavelNome || '—',
              criadoHa: formatarDecorrido(mov.criadoEm),
              venceEm: formatarPrazo(horasRest),
              urgente: horasRest < 6 && horasRest >= 0,
              vencido: horasRest < 0,
              tipoOrigem:
                mov.tipo === 'ESTORNO' ? 'estorno' : 'transferencia',
            },
          });
        });
      });

      return { ...p, posicoes: [...posicoesUI, ...transitoUI] };
    });
  }, [produtos, posicoes, limites, estoquesData, emTransito, parametros]);

  const opcoesResina = useMemo(() => {
    return [...new Set(produtos.map((p) => p.resina).filter(Boolean))].sort();
  }, [produtos]);

  const opcoesEstoque = useMemo(() => {
    return [...new Set(posicoes.map((p) => p.estoqueNome).filter(Boolean))].sort();
  }, [posicoes]);

  const opcoesCor = useMemo(() => {
    return [...new Set(produtos.map((p) => p.cor).filter(Boolean))].sort();
  }, [produtos]);

  const produtosFiltrados = useMemo(() => {
    return produtosComPosicoes.filter((p) => {
      if (busca) {
        const q = busca.toLowerCase();
        const match =
          (p.cor || '').toLowerCase().includes(q) ||
          (p.resina || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filtroResina && p.resina !== filtroResina) return false;
      if (filtroCor && p.cor !== filtroCor) return false;
      if (filtroEstoque && !p.posicoes.some((pos) => pos.estoque === filtroEstoque))
        return false;
      if (filtroStatus) {
        const bate = p.posicoes.some(
          (pos) => pos.status === filtroStatus || pos.statusKey === filtroStatus
        );
        if (!bate) return false;
      }
      return true;
    });
  }, [produtosComPosicoes, busca, filtroResina, filtroCor, filtroEstoque, filtroStatus]);

  const kpis = useMemo(() => {
    const totalProdutos = produtosComPosicoes.length;
    const volumeTotal = produtosComPosicoes.reduce(
      (sum, p) =>
        sum +
        p.posicoes
          .filter((pos) => pos.tipo === 'real')
          .reduce((s, pos) => s + pos.volume, 0),
      0
    );
    const emTransitoQtd = produtosComPosicoes.reduce(
      (sum, p) =>
        sum +
        p.posicoes
          .filter((pos) => pos.tipo === 'transito')
          .reduce((s, pos) => s + pos.qtd, 0),
      0
    );
    const abaixoMinimo = produtosComPosicoes.reduce(
      (sum, p) =>
        sum + p.posicoes.filter((pos) => pos.statusKey === STATUS.CRITICO).length,
      0
    );
    const valorEstimado = produtosComPosicoes.reduce((sum, p) => {
      const preco = Number(p.valorPorLitro) || 0;
      if (!preco) return sum;
      const vol = p.posicoes
        .filter((pos) => pos.tipo === 'real')
        .reduce((s, pos) => s + pos.qtd, 0);
      return sum + preco * vol;
    }, 0);
    return {
      totalProdutos,
      volumeTotal,
      emTransito: emTransitoQtd,
      abaixoMinimo,
      valorEstimado,
    };
  }, [produtosComPosicoes]);

  const totalPosicoes = produtosFiltrados.reduce((sum, p) => sum + p.posicoes.length, 0);
  const totalEmTransito = produtosFiltrados.reduce(
    (sum, p) => sum + p.posicoes.filter((pos) => pos.tipo === 'transito').length,
    0
  );

  function toggleExpandir(id, e) {
    e.stopPropagation();
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  function abrirDrawer(produto) {
    setProdutoSelecionado(produto);
    setAbaAtiva('mov');
  }

  function fecharDrawer() {
    setProdutoSelecionado(null);
  }

  function calcularTotaisReais(posicoesArr) {
    const reais = posicoesArr.filter((p) => p.tipo === 'real');
    return {
      qtd: reais.reduce((sum, p) => sum + p.qtd, 0),
      volume: reais.reduce((sum, p) => sum + p.volume, 0),
      estoques: reais.length,
    };
  }

  const movsDoProdutoSelecionado = useMemo(() => {
    if (!produtoSelecionado) return [];
    return movimentacoes
      .filter((m) =>
        (m.itens || []).some((it) => it.produtoId === produtoSelecionado.id)
      )
      .sort((a, b) => {
        const ta = a.criadoEm?.toDate
          ? a.criadoEm.toDate().getTime()
          : new Date(a.criadoEm || 0).getTime();
        const tb = b.criadoEm?.toDate
          ? b.criadoEm.toDate().getTime()
          : new Date(b.criadoEm || 0).getTime();
        return tb - ta;
      })
      .slice(0, 10);
  }, [produtoSelecionado, movimentacoes]);

  return (
    <>
      <div className={styles.topbar}>
        <div className={styles.pageTitle}>Estoque</div>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.kpis}>
        {/* Total de Produtos — clicável, limpa filtro */}
        <div
          className={styles.kpi}
          onClick={() => setFiltroStatus('')}
          style={{
            cursor: 'pointer',
            outline: filtroStatus === '' ? '2px solid #4A5566' : 'none',
            outlineOffset: -2,
          }}
          title="Limpar filtro de status"
        >
          <div className={styles.kpiLabel}>Total de Produtos</div>
          <div className={styles.kpiValue}>{kpis.totalProdutos}</div>
          <div className={styles.kpiSub}>cadastrados</div>
        </div>

        {/* Volume Total — não clicável */}
        <div className={`${styles.kpi} ${styles.kpiInfo}`}>
          <div className={styles.kpiLabel}>Volume Total</div>
          <div className={styles.kpiValue}>{formatarNumero(kpis.volumeTotal)} L</div>
          <div className={styles.kpiSub}>todos estoques</div>
        </div>

        {/* Em Trânsito — clicável */}
        <div
          className={`${styles.kpi} ${styles.kpiTransit}`}
          onClick={() => setFiltroStatus(filtroStatus === 'Trânsito' ? '' : 'Trânsito')}
          style={{
            cursor: 'pointer',
            outline: filtroStatus === 'Trânsito' ? '2px solid #0F6E56' : 'none',
            outlineOffset: -2,
          }}
          title="Filtrar produtos em trânsito"
        >
          <div className={styles.kpiLabel}>Em Trânsito</div>
          <div className={styles.kpiValue}>{formatarNumero(kpis.emTransito)} L</div>
          <div className={styles.kpiSub}>aguardando aceite</div>
        </div>

        {/* Abaixo do Mínimo — clicável, usa statusKey CRITICO */}
        <div
          className={`${styles.kpi} ${styles.kpiWarn}`}
          onClick={() => setFiltroStatus(filtroStatus === STATUS.CRITICO ? '' : STATUS.CRITICO)}
          style={{
            cursor: 'pointer',
            outline: filtroStatus === STATUS.CRITICO ? '2px solid #A32D2D' : 'none',
            outlineOffset: -2,
          }}
          title="Filtrar produtos abaixo do mínimo"
        >
          <div className={styles.kpiLabel}>Abaixo do Mínimo</div>
          <div className={styles.kpiValue}>{kpis.abaixoMinimo}</div>
          <div className={styles.kpiSub}>
            {kpis.abaixoMinimo === 1 ? 'posição crítica' : 'posições críticas'}
          </div>
        </div>

        <div className={`${styles.kpi} ${styles.kpiInfo}`}>
          <div className={styles.kpiLabel}>Valor Estimado</div>
          <div className={styles.kpiValue} style={{ fontSize: kpis.valorEstimado >= 100000 ? '1.1rem' : undefined }}>
            {kpis.valorEstimado > 0
              ? `R$ ${kpis.valorEstimado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '—'}
          </div>
          <div className={styles.kpiSub}>qtd × valor/litro</div>
        </div>

      </div>

      <div className={styles.filtros}>
        <input
          placeholder="🔍 Buscar produto, resina, cor..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select value={filtroResina} onChange={(e) => setFiltroResina(e.target.value)}>
          <option value="">Resinas</option>
          {opcoesResina.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select value={filtroCor} onChange={(e) => setFiltroCor(e.target.value)}>
          <option value="">Cores</option>
          {opcoesCor.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={filtroEstoque} onChange={(e) => setFiltroEstoque(e.target.value)}>
          <option value="">Estoques</option>
          {opcoesEstoque.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
        <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
          <option value="">Status</option>
          <option value="OK">OK</option>
          <option value="Abaixo mín.">Abaixo mín.</option>
          <option value="Trânsito">Em trânsito</option>
        </select>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {produtosFiltrados.length} produtos · {totalPosicoes} posições
            {totalEmTransito > 0 && (
              <>
                {' · '}
                <span className={styles.totalTransito}>
                  {totalEmTransito} em trânsito
                </span>
              </>
            )}
          </span>
          <span className={styles.tabelaHint}>
            ▶ expande estoques · Clique no produto para detalhes →
          </span>
        </div>

        <div className={styles.colHeader}>
          <div></div>
          <div>Produto</div>
          <div>Resina</div>
          <div>Estoque</div>
          <div>Qtd (L)</div>
          <div>Volume</div>
          <div>Status</div>
        </div>

        {carregando ? (
          <div className={styles.semResultados}>Carregando estoque...</div>
        ) : produtosFiltrados.length === 0 ? (
          <div className={styles.semResultados}>
            {produtos.length === 0
              ? 'Nenhum produto cadastrado ainda.'
              : 'Nenhum produto encontrado com os filtros aplicados.'}
          </div>
        ) : (
          produtosFiltrados.map((produto) => {
            const totais = calcularTotaisReais(produto.posicoes);
            const aberto = expandidos.has(produto.id);
            const alerta = produto.posicoes.some(
              (p) => p.status === 'Abaixo mín.'
            );
            const qtdEmTransito = produto.posicoes
              .filter((p) => p.tipo === 'transito')
              .reduce((sum, p) => sum + p.qtd, 0);

            return (
              <div key={produto.id}>
                <div
                  className={`${styles.prodMaster} ${aberto ? styles.open : ''}`}
                  onClick={() => abrirDrawer(produto)}
                >
                  <div
                    className={styles.toggleBtn}
                    onClick={(e) => toggleExpandir(produto.id, e)}
                    title={aberto ? 'Recolher' : 'Expandir estoques'}
                  >
                    ▶
                  </div>
                  <div>
                    <div className={styles.prodNome}>{produto.cor}</div>
                    <div className={styles.prodCor}>{produto.resina}</div>
                  </div>
                  <div className={styles.prodResinaMarca}>
                    {produto.resina}
                  </div>
                  <div></div>
                  <div></div>
                  <div></div>
                  <div className={styles.prodTotais}>
                    <div className={styles.prodTotalNum}>
                      {totais.qtd} un
                      {totais.volume > 0 && ` · ${formatarNumero(totais.volume)} L`}
                    </div>
                    <div>
                      {alerta ? (
                        <span className={styles.masterAlerta}>
                          {
                            produto.posicoes.filter(
                              (p) => p.status === 'Abaixo mín.'
                            ).length
                          }{' '}
                          alerta
                        </span>
                      ) : (
                        `em ${totais.estoques} ${
                          totais.estoques === 1 ? 'estoque' : 'estoques'
                        }`
                      )}
                      {qtdEmTransito > 0 && (
                        <span className={styles.prodTotaisDetalhe}>
                          {' · '}🔄 {qtdEmTransito} un em trânsito
                        </span>
                      )}
                    </div>
                  </div>
                  <div></div>
                </div>

                <div className={`${styles.subRowsWrap} ${aberto ? styles.open : ''}`}>
                  {produto.posicoes.length === 0 ? (
                    <div className={styles.subRow}>
                      <div className={styles.subIndent}>└</div>
                      <div
                        style={{
                          gridColumn: '2 / 9',
                          color: '#888',
                          fontSize: 12,
                          fontStyle: 'italic',
                        }}
                      >
                        Sem posição de estoque cadastrada.
                      </div>
                    </div>
                  ) : (
                    produto.posicoes.map((pos, idx) => {
                      if (pos.tipo === 'transito') {
                        return (
                          <div
                            key={idx}
                            className={`${styles.subRow} ${styles.transito}`}
                            onClick={() => navigate('/movimentacao')}
                            title="Ver na tela de Movimentação"
                          >
                            <div className={styles.subIndent}>└</div>
                            <div className={styles.subTransitoInfo}>
                              <strong>
                                {pos.transito.origem} → {pos.transito.destino}
                              </strong>
                              {pos.transito.tipoOrigem === 'estorno'
                                ? ' · retorno de negação'
                                : ` · criado por ${pos.transito.iniciador}`}
                              {' · '}
                              {pos.transito.criadoHa}
                              <span
                                className={`${styles.prazo} ${
                                  pos.transito.urgente || pos.transito.vencido
                                    ? styles.urgente
                                    : ''
                                }`}
                              >
                                {pos.transito.vencido
                                  ? '⚠ vencido'
                                  : pos.transito.urgente
                                  ? '⚠ vence em ' + pos.transito.venceEm
                                  : '⏱ vence em ' + pos.transito.venceEm}
                              </span>
                            </div>
                            <div className={styles.subEstoqueCell}>
                              <span
                                className={`${styles.badgeEstoque} ${styles.badgeTransito}`}
                              >
                                🔄 Em transferência
                              </span>
                            </div>
                            <div className={styles.subQtd}>{pos.qtd} L</div>
                            <div className={styles.subVol}>—</div>
                            <div>
                              <span
                                className={`${styles.statusBadge} ${styles[STATUS_CLASS[pos.status]]}`}
                              >
                                {pos.status}
                              </span>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={idx} className={styles.subRow}>
                          <div className={styles.subIndent}>└</div>
                          <div></div>
                          <div></div>
                          <div className={styles.subEstoqueCell}>
                            <span className={styles.badgeEstoque}>
                              {pos.estoque}
                            </span>
                          </div>
                          <div className={styles.subQtd}>{pos.qtd} L</div>
                          <div className={styles.subVol}>
                            {pos.volume > 0
                              ? `${formatarNumero(pos.volume)} L`
                              : '—'}
                          </div>
                          <div>
                            <span
                              className={`${styles.statusBadge} ${styles[STATUS_CLASS[pos.status]]}`}
                            >
                              {pos.status}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {produtoSelecionado && (
        <>
          <div
            className={`${styles.drawerOverlay} ${styles.open}`}
            onClick={fecharDrawer}
          />
          <div className={`${styles.drawer} ${styles.open}`}>
            <div className={styles.drawerHead}>
              <div className={styles.drawerHeadTop}>
                <div>
                  <div className={styles.drawerProdNome}>
                    {produtoSelecionado.cor}
                  </div>
                  <div className={styles.drawerProdCor}>
                    {produtoSelecionado.resina}
                  </div>
                </div>
                <button className={styles.drawerClose} onClick={fecharDrawer} aria-label="Fechar">
                  ✕
                </button>
              </div>
              <div className={styles.drawerTags}>
                {produtoSelecionado.resina && (
                  <span className={styles.drawerTag}>
                    {produtoSelecionado.resina}
                  </span>
                )}
              </div>
            </div>

            {produtoSelecionado.orientacao && (
              <div className={styles.drawerMeta}>
                <div className={styles.metaTitle}>Orientação de uso</div>
                <div className={styles.metaContent}>
                  {produtoSelecionado.orientacao}
                </div>
              </div>
            )}

            <div className={styles.drawerTabs}>
              <div
                className={`${styles.drawerTab} ${
                  abaAtiva === 'mov' ? styles.active : ''
                }`}
                onClick={() => setAbaAtiva('mov')}
              >
                Movimentações
              </div>
            </div>

            <div className={styles.drawerBody}>
              {abaAtiva === 'mov' && (
                <>
                  {movsDoProdutoSelecionado.length === 0 ? (
                    <div
                      style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#888',
                        fontSize: 12,
                      }}
                    >
                      Nenhuma movimentação registrada para este produto.
                    </div>
                  ) : (
                    movsDoProdutoSelecionado.map((m) => {
                      const item = (m.itens || []).find(
                        (i) => i.produtoId === produtoSelecionado.id
                      );
                      const qtd = item?.qtd || 0;
                      const corQtd =
                        m.tipo === 'SAIDA'
                          ? '#A32D2D'
                          : m.tipo === 'ENTRADA'
                          ? '#0F6E56'
                          : '#534AB7';
                      const resumo =
                        m.tipo === 'SAIDA'
                          ? `${m.os || 'OS'} · ${m.estoqueOrigemNome || m.estoqueNome || ''}`
                          : m.tipo === 'ENTRADA'
                          ? `${m.nf || 'NF'} · ${m.estoqueDestinoNome || m.estoqueNome || ''}`
                          : m.tipo === 'TRANSFERENCIA' || m.tipo === 'ESTORNO'
                          ? `${m.estoqueOrigemNome || ''} → ${m.estoqueDestinoNome || ''}`
                          : `${m.os || 'OS'} → ${m.estoqueDestinoNome || ''}`;
                      return (
                        <div key={m.id} className={styles.movItem}>
                          <span
                            className={`${styles.movTag} ${styles[MOV_TAG_CLASS[m.tipo]] || ''}`}
                          >
                            {MOV_LABEL[m.tipo] || m.tipo}
                          </span>
                          <div className={styles.movMeta}>
                            <div className={styles.movOs}>{resumo}</div>
                            <div className={styles.movData}>
                              {formatarDataMov(m.criadoEm)}
                              {m.responsavelNome && ` · ${m.responsavelNome}`}
                              {m.status === 'EM_TRANSITO' && ' · em trânsito'}
                              {m.status === 'NEGADA' && ' · negada'}
                            </div>
                          </div>
                          <span
                            className={styles.movQtd}
                            style={{ color: corQtd }}
                          >
                            {m.tipo === 'SAIDA' ? '-' : '+'}
                            {qtd}
                          </span>
                        </div>
                      );
                    })
                  )}
                  <div
                    className={styles.verMaisLink}
                    onClick={() => navigate('/movimentacao/historico')}
                  >
                    Ver histórico completo →
                  </div>
                </>
              )}


            </div>

            <div className={styles.drawerFoot}>
              <button
                className={styles.btnMovProd}
                onClick={() =>
                  navigate(`/movimentacao?produto=${produtoSelecionado.id}`)
                }
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 2v12M3 7l5-5 5 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 14h10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Registrar movimentação
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

